import { Outlet } from "react-router-dom";
import { ReactRouterAppProvider } from "@toolpad/core/react-router";
import { AccountProps, AppProviderProps, Branding, Navigation } from "@toolpad/core";
import { LocalizationProvider } from '@toolpad/core/AppProvider'
import { Theme, ThemeProvider } from "@mui/material";
import * as React from "react";
import { createContext, useCallback, useContext, useEffect } from "react";
import { SessionContext } from "../authentication/SessionProvider";
import Session from "../authentication/Session";
import { User } from "../api-client/AppFwApiClient";
import { BrandingControl, BrandingControlContext } from "./branding/BrandingControl.tsx";


// TODO: install from somewhere (or use "resource bundle")?
// See: https://mui.com/toolpad/core/api/localization-provider/
const deDeToolpadLocaleText = {
    ...LocalizationProvider,

    accountSignOutLabel: "Abmelden",
}

// some custom extension + real provider (toolpad does not offer a public context)
export interface AppFwAppProviderProps extends AppProviderProps {
    readonly layout: "default" | "mobile";
    readonly accountEl?: React.JSXElementConstructor<AccountProps>;
}

export const AppFwAppProviderPropsContext =
    createContext<AppFwAppProviderProps>({
        layout: "default",
        children: <></>,
    })


interface AppFwAppProps {
    readonly theme: Theme;
    readonly navigation?: Navigation;
    readonly defaultBranding?: Branding;
    readonly layout?: "default" | "mobile";
    readonly accountEl?: React.JSXElementConstructor<AccountProps>;
}

import { useAppFwContext } from "../AppFwContextContextProvider.tsx";

export default function AppFwApp(props: AppFwAppProps) {

    const session = useContext<undefined | null | Session>(SessionContext)
    const appFwContext = useAppFwContext()

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                appFwContext.triggerRefresh().catch(console.error)
            }
        }
        document.addEventListener("visibilitychange", handleVisibilityChange)
        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange)
        }
    }, [appFwContext])


    const [branding, setBranding] = React.useState<undefined | Branding>(props.defaultBranding)

    const setSuffix = useCallback(
        (suffix: string) => {
            let baseTitle = props.defaultBranding?.title
            if (baseTitle) {
                setBranding(prev => ({
                    ...prev,
                    title: `${baseTitle} » ${suffix}`,
                }))
            } else {
                setBranding(prev => ({
                    ...prev,
                    title: suffix,
                }))
            }
        }, [branding, setBranding, props.defaultBranding])

    const resetBranding = useCallback(() => {
        setBranding(props.defaultBranding)
    }, [branding, setBranding, props.defaultBranding])

    const brandingControl: BrandingControl = {
        setSuffix,
        resetBranding
    };

    let toolpadAuthentication = {
        signIn: () => {
            console.log("signIn?? Should not be called")
        },
        signOut: async (path?: string) => {
            await session?.logout(path)
        },
    }

    let toolpadSession: null | Session = null
    if (session != null) {
        let toolpadSessionUser: User & { image?: string } = {
            ...session.user,
            image: session.user.avatarUrl,
            // TODO: email support?
        }
        toolpadSession = {
            ...session,
            // id: session.id,
            user: toolpadSessionUser,
            logout: toolpadAuthentication.signOut,
        }
    }

    const appFwAppProviderProps: AppFwAppProviderProps = {
        children: <Outlet />,

        // Toolpad.AppProviderProps
        authentication: toolpadAuthentication,
        branding: branding,
        localeText: deDeToolpadLocaleText,
        navigation: props.navigation,
        session: toolpadSession,
        theme: props.theme,

        // AppFw extensions
        accountEl: props.accountEl,
        layout: props.layout ?? "default",
    }

    return (
        <ThemeProvider theme={props.theme}>
            <BrandingControlContext.Provider value={brandingControl}>
                <AppFwAppProviderPropsContext.Provider value={appFwAppProviderProps}>
                    <ReactRouterAppProvider {...appFwAppProviderProps} />
                </AppFwAppProviderPropsContext.Provider>
            </BrandingControlContext.Provider>
        </ThemeProvider>
    )
}